import { ChangeEvent, MouseEventHandler, useEffect, useState } from 'react';
import Icon, { SwapRightOutlined } from '@ant-design/icons';
import { Form } from 'antd';
import { FormInstance, Rule } from 'antd/lib/form';

import If from 'global/ErrorHandlers/If';
import InfoSvg from 'global/Securities/components/Icons/InfoSvg';
import InputNumberAddon from 'global/Securities/components/Input/InputNumberAddon';
import Switch from 'global/Securities/components/Switch';
import {
  orderMaxLot,
  orderMaxQuantity,
} from 'global/Securities/constants/transaction';
import {
  SplitOrderMethodEnum,
  TransactionType,
} from 'global/Securities/types/transaction';
import realTradingStore from 'global/TradingModal/RealTradingModal/store';
import useSplitOrderValidation from './useSplitOrderValidation';

import {
  FormItem,
  FormItemError,
  FormItemRange,
  InfoButton,
  QuantityAddon,
  RangeInput,
  RangeWrapper,
  RadioButton,
  RadioButtonContainer,
} from './styled';

interface SplitOrderFormItemProps {
  form: FormInstance;
  // TODO: Change buyOrderLot to orderLot for more general and precise naming
  orderLot?: number;

  minQuantity?: number;
  maxQuantity?: number;
  minRange?: number;
  maxRange?: number;

  checkedInitialValue?: boolean;
  quantityInitialValue?: number;
  minRangeInitialValue?: number;
  maxRangeInitialValue?: number;

  transactionType?: TransactionType;

  disabledToggle?: boolean;

  onInfoIconClick?: MouseEventHandler<HTMLButtonElement>;
  onSwitchChange?: (value: boolean) => void;
  onSplitMethodInfoClick?: MouseEventHandler<HTMLButtonElement>;
}

const SplitOrderFormItem = ({
  form,
  orderLot = 10,

  minQuantity = 0,
  maxQuantity = orderMaxQuantity,
  minRange = 0,
  maxRange = orderMaxLot,

  checkedInitialValue = false,
  quantityInitialValue = 0,
  minRangeInitialValue,
  maxRangeInitialValue,

  transactionType = TransactionType.Buy,

  disabledToggle = false,

  onInfoIconClick = () => {},
  onSwitchChange = () => {},
  onSplitMethodInfoClick = () => {},
}: SplitOrderFormItemProps) => {
  const { splitMethod, setSplitMethod, setSplitRange } = realTradingStore();
  const [isSplitOrderActive, setSplitOrderActive] =
    useState(checkedInitialValue);
  const {
    getValidateQuantityField,
    getValidateMinRangeField,
    getValidateMaxRangeField,
  } = useSplitOrderValidation(transactionType);

  const [quantityValue, setQuantityValue] = useState(0);

  const handleValidateAllFields = () => {
    form.validateFields();
  };

  const handleSwitchChange = (value: boolean) => {
    form.resetFields(['splitQuantity', 'splitRangeMin', 'splitRangeMax']);

    setSplitOrderActive(value);
    handleValidateAllFields();

    onSwitchChange(value);
  };

  const handleQuantityChange = (value: number) => {
    setQuantityValue(value);
    handleValidateAllFields();
  };

  const handleInfoIconClick: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.stopPropagation();
    onInfoIconClick(event);
  };

  const handleValidateQuantity: Rule = ({ getFieldsValue, setFields }) => ({
    validator: async () => {
      const { splitQuantity, splitRangeMin, splitRangeMax } = getFieldsValue([
        'splitQuantity',
        'splitRangeMin',
        'splitRangeMax',
      ]);
      const isFieldsEmpty = !(
        splitQuantity !== undefined &&
        splitRangeMin &&
        splitRangeMax
      );
      const isBuyOrderLotEmpty = orderLot === 0;

      setFields(
        ['splitRangeMin', 'splitRangeMax'].map((name) => ({
          name,
          errors: undefined,
        })),
      );

      if (isFieldsEmpty || isBuyOrderLotEmpty) {
        return Promise.resolve();
      }

      const errorItem = getValidateQuantityField(
        splitQuantity,
        splitRangeMin,
        splitRangeMax,
        orderLot,
      );

      if (errorItem?.errorCondition) {
        if (errorItem.message.includes('lower')) {
          form.setFieldsValue({ splitRangeMin: 1, splitRangeMax: 1 });
        }

        return Promise.reject(
          errorItem.message ? new Error(errorItem.message) : undefined,
        );
      }
      return Promise.resolve();
    },
  });

  const handleValidateRange: Rule = ({ setFields, getFieldsValue }) => ({
    validator: async () => {
      const { splitQuantity, splitRangeMin, splitRangeMax } = getFieldsValue([
        'splitQuantity',
        'splitRangeMin',
        'splitRangeMax',
      ]);
      const isFieldsEmpty = !(
        splitQuantity !== undefined &&
        splitRangeMin &&
        splitRangeMax
      );
      const isOrderLotEmpty = orderLot === 0;

      setFields(
        ['splitRangeMin', 'splitRangeMax'].map((name) => ({
          name,
          errors: undefined,
        })),
      );

      if (isFieldsEmpty || isOrderLotEmpty) {
        return Promise.resolve();
      }

      const quantityFieldError = getValidateQuantityField(
        splitQuantity,
        splitRangeMin,
        splitRangeMax,
        orderLot,
      );

      if (quantityFieldError?.message) return Promise.resolve();

      const minRangeFieldError = getValidateMinRangeField(
        splitQuantity,
        splitRangeMin,
        splitRangeMax,
        orderLot,
      );

      const maxRangeFieldError = getValidateMaxRangeField(
        splitQuantity,
        splitRangeMin,
        splitRangeMax,
        orderLot,
      );

      if (minRangeFieldError || maxRangeFieldError) {
        if (minRangeFieldError) {
          setFields([{ name: 'splitRangeMin', errors: [''] }]);
        }
        if (maxRangeFieldError) {
          setFields([{ name: 'splitRangeMax', errors: [''] }]);
        }
        return Promise.reject(minRangeFieldError || maxRangeFieldError);
      }

      return Promise.resolve();
    },
  });

  const handleResetRange = () => {
    form.setFieldsValue({
      splitRangeMin: 0,
      splitRangeMax: 0,
    });
    setSplitRange(0, 0);
  };

  const handleSplitMethodSelected = (e: ChangeEvent<HTMLInputElement>) => {
    setSplitMethod(e.target.value as SplitOrderMethodEnum);

    if (e.target.value === SplitOrderMethodEnum.EQUAL) {
      handleResetRange();
    }

    setTimeout(() => {
      handleValidateAllFields();
    }, 0);
  };

  // set value setMinRange and setMaxRange if orderLot or splitQuantity change
  const handleSetMinMaxRange = () => {
    const { splitQuantity } = form.getFieldsValue([
      'splitQuantity',
      'splitRangeMin',
      'splitRangeMax',
    ]);

    if (splitMethod === SplitOrderMethodEnum.EQUAL) {
      handleResetRange();
      return;
    }

    if (!splitQuantity || !orderLot) {
      form.setFieldsValue({
        splitRangeMin: 0,
        splitRangeMax: 0,
      });
      setSplitRange(0, 0);

      return;
    }

    const orderPerQuantity = orderLot / splitQuantity;
    const formattedOrderPerQuantity =
      orderPerQuantity > 1 ? orderPerQuantity : 1;

    // Minimum Lot Size as 50% of the average lot size, rounding down any decimal result.
    // Maximum Lot Size as 150% of the average lot size, rounding up any decimal result.
    const calculadteMin = Math.floor(formattedOrderPerQuantity * 0.5);
    const calculadteMax = Math.ceil(formattedOrderPerQuantity * 1.5);

    const formattedMin = calculadteMin > 0 ? calculadteMin : 1;

    const rangeMin = formattedMin > orderMaxLot ? orderMaxLot : formattedMin;
    const rangeMax = calculadteMax > orderMaxLot ? orderMaxLot : calculadteMax;

    form.setFieldsValue({
      splitRangeMin: rangeMin,
      splitRangeMax: rangeMax,
    });
    setSplitRange(rangeMin, rangeMax);
  };

  useEffect(() => {
    handleSetMinMaxRange();

    if (!isSplitOrderActive) {
      setQuantityValue(0);
    }
  }, [orderLot, quantityValue, isSplitOrderActive, splitMethod]);

  return (
    <>
      <Form.Item
        label={
          <>
            Split Order
            <InfoButton type="button" onClick={handleInfoIconClick}>
              <Icon
                component={InfoSvg}
                style={{ fontSize: 16, color: '#969696', marginLeft: 4 }}
              />
            </InfoButton>
          </>
        }
        name="isSplitOrderActive"
        initialValue={checkedInitialValue}
        valuePropName="checked"
      >
        <Switch onChange={handleSwitchChange} disabled={disabledToggle} />
      </Form.Item>

      {isSplitOrderActive && (
        <>
          <FormItem
            label="Split Quantity"
            name="splitQuantity"
            validateTrigger="onChange"
            rules={[handleValidateQuantity]}
            initialValue={quantityInitialValue}
          >
            <InputNumberAddon
              addonAfter={
                <QuantityAddon>order{quantityValue > 1 && 's'}</QuantityAddon>
              }
              min={minQuantity}
              max={maxQuantity}
              formatter="0,0"
              autoComplete="off"
              onChange={handleQuantityChange}
              disableHighlight
              disableDecimal
            />
          </FormItem>
          <FormItem
            label={
              <>
                <span>Split Method</span>
                <InfoButton type="button" onClick={onSplitMethodInfoClick}>
                  <Icon
                    component={InfoSvg}
                    style={{ fontSize: 16, color: '#969696', marginLeft: 4 }}
                  />
                </InfoButton>
              </>
            }
          >
            <RadioButtonContainer>
              <RadioButton htmlFor="radio-random">
                <input
                  type="radio"
                  id="radio-random"
                  name="splitMethod"
                  value={SplitOrderMethodEnum.RANDOM}
                  checked={splitMethod === SplitOrderMethodEnum.RANDOM}
                  onChange={handleSplitMethodSelected}
                  defaultChecked
                />
                <span>Random</span>
              </RadioButton>

              <RadioButton htmlFor="radio-equal">
                <input
                  type="radio"
                  id="radio-equal"
                  name="splitMethod"
                  value={SplitOrderMethodEnum.EQUAL}
                  checked={splitMethod === SplitOrderMethodEnum.EQUAL}
                  onChange={handleSplitMethodSelected}
                />
                <span>Equal</span>
              </RadioButton>
            </RadioButtonContainer>
          </FormItem>

          <If condition={splitMethod === SplitOrderMethodEnum.RANDOM}>
            <FormItemError
              validateStatus=""
              label="Split Range Lot"
              name="rangeInput"
              validateTrigger="onChange"
              rules={[handleValidateRange]}
              initialValue
            >
              <RangeWrapper>
                <FormItemRange
                  name="splitRangeMin"
                  initialValue={minRangeInitialValue}
                >
                  <RangeInput
                    min={minRange}
                    max={maxRange}
                    placeholder="Min"
                    formatter="0,0"
                    autoComplete="off"
                    zeroReplacer=""
                    onChange={handleValidateAllFields}
                    allowEmptyField
                    disableDecimal
                    required
                  />
                </FormItemRange>
                <SwapRightOutlined style={{ color: '#96969699' }} />
                <FormItemRange
                  name="splitRangeMax"
                  initialValue={maxRangeInitialValue}
                >
                  <RangeInput
                    min={minRange}
                    max={maxRange}
                    placeholder="Max"
                    formatter="0,0"
                    autoComplete="off"
                    zeroReplacer=""
                    onChange={handleValidateAllFields}
                    allowEmptyField
                    disableDecimal
                    required
                  />
                </FormItemRange>
              </RangeWrapper>
            </FormItemError>
          </If>
        </>
      )}
    </>
  );
};

export default SplitOrderFormItem;
